import React, {
    useState,
    useCallback,
    useMemo,
    useRef,
    FC,
    useEffect,
} from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import clsx from 'clsx';
import ReactDOM from 'react-dom';

// Import components
import ViewerHeader from './components/ViewerHeader';
import SearchBar from './components/SearchBar';
import StateContent from './components/StateContent';
import ResizeHandle from './components/ResizeHandle';
import PathDisplay from './components/PathDisplay';
import EmptyStateView from './components/EmptyStateView';
import ContextMenu from './components/ContextMenu';

// Import contexts
import {
    ContextMenuProvider,
    useContextMenu,
} from './contexts/ContextMenuContext';

// Import utilities and hooks
import { sortStateObject } from './utils/sortUtils';
import { sectionContainsMatch } from './utils/searchUtils';
import { useDebounce } from './hooks/useDebounce';
import { useResizable } from './hooks/useResizable';
import { useDraggable } from './hooks/useDraggable';
import { useStateSearch } from './hooks/useStateSearch';
import { useMergeRefs } from 'flow/hooks/useMergeRefs';

// Define interfaces for section items and component state
interface SectionItem {
    key: string;
    value: any;
    isMatch: boolean;
    isExpanded: boolean;
}

/**
 * Main StateViewer component for viewing Redux state
 * @returns {JSX.Element} - Rendered component
 */
const StateViewer: FC = () => {
    // Access the Redux state
    const state = useAppSelector((s: any) => s);

    // Basic state
    const [isMinimized, setIsMinimized] = useState<boolean>(true);
    const [maximizedPosition, setMaximizedPosition] = useState<
        'left' | 'right'
    >('right');
    const [expandedSections, setExpandedSections] = useState<
        Record<string, boolean>
    >({});
    const [inPopup, setInPopup] = useState(false);

    // Sort the state before rendering
    const sortedState = useMemo(() => sortStateObject(state), [state]);

    // Path tracking state
    const [hoveredPath, setHoveredPath] = useState<string>('');
    const debouncedHoveredPath = useDebounce(hoveredPath, 50);
    const isHovering = useRef<boolean>(false);

    // Use custom hooks for different functionality
    const {
        viewerWidth,
        isResizing,
        elementRef: resizeRef,
        handleResizeStart,
    } = useResizable(40, isMinimized, maximizedPosition);

    const {
        position,
        isDragging,
        dragPosition,
        elementRef: dragRef,
        handleMouseDown,
    } = useDraggable(isMinimized);

    const {
        inputValue,
        debouncedSearchTerm,
        searchResults,
        matchingPaths,
        handleSearchInputChange,
        clearSearch,
        parseSearchPath,
    } = useStateSearch(sortedState, setExpandedSections);

    // Combine refs to use both resize and drag functionality
    const viewerRef = useMergeRefs(resizeRef, dragRef);

    // Toggle the position of the maximized viewer between left and right
    const toggleMaximizedPosition = useCallback(() => {
        setMaximizedPosition((prev) => (prev === 'right' ? 'left' : 'right'));
    }, []);

    // Toggle minimized state
    const toggleMinimize = useCallback(() => {
        setIsMinimized((prev) => !prev);
    }, []);

    // Function to collapse all sections and clear search
    const collapseAll = useCallback(() => {
        setExpandedSections({});
        clearSearch();
    }, [clearSearch]);

    // Function to toggle section expansion
    const toggleSection = useCallback((key: string) => {
        setExpandedSections((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    }, []);

    // Mouse enter/leave handlers for the entire viewer
    const handleViewerMouseEnter = useCallback(() => {
        isHovering.current = true;
    }, []);

    const handleViewerMouseLeave = useCallback(() => {
        isHovering.current = false;
        setHoveredPath('');
    }, []);

    // Wrapper for search input change to handle string input and event properly
    const handleSearchChange = useCallback(
        (input: string | React.ChangeEvent<HTMLInputElement>) => {
            handleSearchInputChange(input);
        },
        [handleSearchInputChange],
    );

    // Calculate sections to render with memoization for performance
    const sectionsToRender = useMemo((): SectionItem[] => {
        if (!debouncedSearchTerm) {
            // When no search term, show all sections
            return Object.entries(sortedState).map(([key, value]) => ({
                key,
                value,
                isMatch: false,
                isExpanded: expandedSections[key],
            }));
        }

        const searchPath = parseSearchPath(debouncedSearchTerm);

        return Object.entries(sortedState)
            .filter(([key, value]) =>
                sectionContainsMatch(key, value, searchPath),
            )
            .map(([key, value]) => {
                const isKeyMatch = key
                    .toLowerCase()
                    .includes(searchPath.toLowerCase());
                const mightContainValue = true; // simplified for brevity
                const isIdSearch =
                    searchPath.includes('-') || /^\d+$/.test(searchPath);
                const mightContainId =
                    isIdSearch &&
                    [
                        'chats',
                        'messages',
                        'users',
                        'contacts',
                        'conversations',
                    ].includes(key.toLowerCase());
                const isSearchPathStart =
                    searchPath.split('.')[0] === key.toLowerCase();

                // Always expand the top-level section that matches the search
                const isExpanded =
                    expandedSections[key] ||
                    isKeyMatch ||
                    isSearchPathStart ||
                    mightContainId;

                return {
                    key,
                    value,
                    isMatch: isKeyMatch || mightContainValue || mightContainId,
                    isExpanded,
                };
            });
    }, [sortedState, debouncedSearchTerm, expandedSections, parseSearchPath]);

    // Get the context menu functionality - add openContextMenu
    const { activeContextMenu, closeAllContextMenus, openContextMenu } =
        useContextMenu();

    // Add this handler for right-click events on the StateViewer
    const handleContextMenu = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const menuItems = [
                {
                    label: 'Collapse All',
                    action: collapseAll,
                },
                {
                    label: `Toggle Position (${maximizedPosition})`,
                    action: toggleMaximizedPosition,
                },
                {
                    label: isMinimized ? 'Maximize' : 'Minimize',
                    action: toggleMinimize,
                },
                {
                    label: 'Clear Search',
                    action: clearSearch,
                },
            ];

            openContextMenu({
                x: e.clientX,
                y: e.clientY,
                menuItems,
            });
        },
        [
            collapseAll,
            toggleMaximizedPosition,
            toggleMinimize,
            isMinimized,
            clearSearch,
            maximizedPosition,
            openContextMenu,
        ],
    );

    // Open popup and render portal
    const popupWindowRef = useRef<Window | null>(null);
    const popupContainerRef = useRef<HTMLDivElement | null>(null);

    const openInPopup = useCallback(() => {
        if (popupWindowRef.current && !popupWindowRef.current.closed) {
            popupWindowRef.current.focus();
            return;
        }
        const popup = window.open(
            '',
            'StateViewerPopup',
            'width=900,height=800,left=200,top=100',
        );
        if (!popup) return;
        popup.document.title = 'State Viewer';
        // Inject main.css for Tailwind and app styles
        // Try to find the main stylesheet from the current document
        // Copy all <style> elements from the current document to the popup
        Array.from(document.querySelectorAll('style')).forEach((style) => {
            const newStyle = popup.document.createElement('style');
            newStyle.textContent = style.textContent;
            popup.document.head.appendChild(newStyle);
        });

        // Create container for portal
        const container = popup.document.createElement('div');
        popup.document.body.appendChild(container);
        popupContainerRef.current = container;
        popupWindowRef.current = popup;
        setInPopup(true);
        // Cleanup when popup closes
        const timer = setInterval(() => {
            if (popup.closed) {
                setInPopup(false);
                popupWindowRef.current = null;
                popupContainerRef.current = null;
                clearInterval(timer);
            }
        }, 500);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (popupWindowRef.current && !popupWindowRef.current.closed) {
                popupWindowRef.current.close();
            }
        };
    }, []);

    // The actual viewer content
    const viewerContent = (
        <div
            ref={viewerRef}
            className={clsx(
                'fixed z-[99999] text-black',
                isMinimized
                    ? 'cursor-grab'
                    : 'h-screen overflow-auto bg-white font-mono shadow-lg',
                isMinimized && !isDragging && position.x,
                isMinimized && !isDragging && position.y,
                !isMinimized &&
                    !inPopup &&
                    (maximizedPosition === 'right' ? 'end-0' : 'start-0'),
                !isMinimized && !inPopup && 'top-0',
                isResizing && 'cursor-ew-resize',
            )}
            style={{
                ...(isDragging && isMinimized
                    ? {
                          position: 'fixed',
                          left: `${dragPosition.x}px`,
                          top: `${dragPosition.y}px`,
                          cursor: 'grabbing',
                      }
                    : {}),
                ...(!isMinimized
                    ? inPopup
                        ? {
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              width: '100vw',
                              height: '100vh',
                              minWidth: 0,
                          }
                        : {
                              width: `${viewerWidth}vw`,
                              minWidth: '400px',
                          }
                    : {}),
            }}
            onMouseEnter={handleViewerMouseEnter}
            onMouseLeave={handleViewerMouseLeave}
            onContextMenu={handleContextMenu}
        >
            <ViewerHeader
                isMinimized={isMinimized}
                maximizedPosition={maximizedPosition}
                toggleMaximizedPosition={toggleMaximizedPosition}
                collapseAll={collapseAll}
                toggleMinimize={toggleMinimize}
                onMouseDown={isMinimized ? handleMouseDown : undefined}
                onPopout={openInPopup}
            />

            {!isMinimized && (
                <>
                    <SearchBar
                        searchTerm={inputValue}
                        onSearchChange={handleSearchChange}
                        results={sectionsToRender.length}
                    />

                    <StateContent
                        sectionsToRender={sectionsToRender}
                        debouncedSearchTerm={debouncedSearchTerm}
                        toggleSection={toggleSection}
                        parseSearchPath={parseSearchPath}
                        searchResults={searchResults}
                        matchingPaths={matchingPaths}
                        setHoveredPath={setHoveredPath}
                    />

                    <ResizeHandle
                        maximizedPosition={maximizedPosition}
                        onMouseDown={handleResizeStart}
                    />

                    <PathDisplay
                        isVisible={
                            !isMinimized &&
                            debouncedHoveredPath &&
                            isHovering.current
                        }
                        debouncedHoveredPath={debouncedHoveredPath}
                        viewerWidth={viewerWidth}
                        maximizedPosition={maximizedPosition}
                    />
                </>
            )}

            {activeContextMenu && (
                <ContextMenu
                    x={activeContextMenu.x}
                    y={activeContextMenu.y}
                    menuItems={
                        activeContextMenu.menuItems as Array<{
                            label: string;
                            action: () => void;
                        }>
                    }
                    onClose={closeAllContextMenus}
                />
            )}
        </div>
    );

    // Handle the case where state is empty or invalid
    if (!state || Object.keys(state).length === 0) {
        return <EmptyStateView />;
    }

    // Render in portal if inPopup, else normal
    if (inPopup && popupContainerRef.current) {
        return ReactDOM.createPortal(viewerContent, popupContainerRef.current);
    }
    return viewerContent;
};

// Properly wrap and export the component
const StateViewerWithProvider: FC = () => (
    <ContextMenuProvider>
        <StateViewer />
    </ContextMenuProvider>
);

export default StateViewerWithProvider;
